import { logger } from '@talos/kyoko';
import type { TCounterparty } from '../../containers/Admin/Counterparties/types';

export const INVALID_COUNTERPARTY_CACHE_KEY_PREFIX = '__invalid-counterparty-';

const INVALID_COUNTERPARTY_SAMPLE_LIMIT = 10;

const warnedInvalidCounterpartyIds = new Set<number>();

export type NormalizedCounterparty = TCounterparty & { Name: string };
type CounterpartyStreamRow = Omit<TCounterparty, 'Name'> & { Name?: string | null };

function isUsableCounterpartyName(name: string | null | undefined): name is string {
  return typeof name === 'string' && name.trim() !== '';
}

export function getCounterpartyCacheKey(
  item: Pick<TCounterparty, 'CounterpartyID'> & { Name?: string | null }
): string {
  return isUsableCounterpartyName(item.Name)
    ? item.Name
    : `${INVALID_COUNTERPARTY_CACHE_KEY_PREFIX}${item.CounterpartyID}`;
}

function hasValidCounterpartyName(item: CounterpartyStreamRow): item is NormalizedCounterparty {
  return isUsableCounterpartyName(item.Name);
}

export function normalizeCounterpartiesForCache(data: CounterpartyStreamRow[]): NormalizedCounterparty[] {
  const normalizedItems: NormalizedCounterparty[] = [];
  const newlyInvalidItems: CounterpartyStreamRow[] = [];

  for (const item of data) {
    if (hasValidCounterpartyName(item)) {
      normalizedItems.push(item);
      warnedInvalidCounterpartyIds.delete(item.CounterpartyID);
    } else {
      if (!warnedInvalidCounterpartyIds.has(item.CounterpartyID)) {
        newlyInvalidItems.push(item);
        warnedInvalidCounterpartyIds.add(item.CounterpartyID);
      }
      normalizedItems.push({ ...item, Name: '' });
    }
  }

  if (newlyInvalidItems.length > 0) {
    const sampleLimit = Math.min(newlyInvalidItems.length, INVALID_COUNTERPARTY_SAMPLE_LIMIT);
    logger.warn('Counterparty stream rows missing Name; falling back to empty Name', {
      extra: {
        invalidCount: newlyInvalidItems.length,
        invalidCounterpartyIds: newlyInvalidItems.slice(0, sampleLimit).map(item => item.CounterpartyID),
        invalidNameValues: newlyInvalidItems.slice(0, sampleLimit).map(item => item.Name),
        omittedInvalidCount: newlyInvalidItems.length - sampleLimit,
      },
    });
  }

  return normalizedItems;
}

export function addCounterpartyToCache(
  map: Map<string, TCounterparty>,
  item: NormalizedCounterparty
): Map<string, TCounterparty> {
  const cacheKey = getCounterpartyCacheKey(item);

  const existingAtKey = map.get(cacheKey);
  if (existingAtKey?.CounterpartyID === item.CounterpartyID) {
    return map.set(cacheKey, item);
  }

  const syntheticKey = getCounterpartyCacheKey({ CounterpartyID: item.CounterpartyID, Name: '' });
  if (cacheKey !== syntheticKey && map.get(syntheticKey)?.CounterpartyID === item.CounterpartyID) {
    map.delete(syntheticKey);
  } else if (cacheKey === syntheticKey) {
    // Only scan for rare valid-to-invalid recovery; normal updates stay O(1).
    for (const [key, cachedItem] of map) {
      if (key !== cacheKey && cachedItem.CounterpartyID === item.CounterpartyID) {
        map.delete(key);
      }
    }
  }

  return map.set(cacheKey, item);
}
